import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {buttonClasses, Grow, MenuList, Paper, Popper} from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import {TableFilterButtonType} from "@/store/dataGrid.c";

export interface FilterButtonProps {
    title: string;
    items: TableFilterButtonType[];
    handleSelect?: (item: TableFilterButtonType) => void;
    startIcon?: React.ReactNode;
    mode: "multiple" | "single";
}

const FilterButton: React.FC<FilterButtonProps> = ({
                                                       title,
                                                       items,
                                                       mode,
                                                       handleSelect,
                                                       startIcon,
                                                   }) => {
    const [open, setOpen] = useState<boolean>(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (
        event: Event | React.SyntheticEvent,
        item: TableFilterButtonType,
        selected = true,
    ) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        if (handleSelect && selected) {
            handleSelect(item);
        }
        setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === "Tab") {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === "Escape") {
            setOpen(false);
        }
    }

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current!.focus();
        }

        prevOpen.current = open;
    }, [open]);

    const MenuPopper = () => {
        return (
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom-start"
                sx={{
                    zIndex: 1300,
                    borderRadius: "8px",
                    border: "1px solid var(--template-palette-divider)",
                }}
                transition
                disablePortal
            >
                {({TransitionProps, placement}) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === "bottom-start" ? "left top" : "left bottom",
                        }}
                    >
                        <Paper>
                            <ClickAwayListener
                                onClickAway={(event) => handleClose(event, {} as TableFilterButtonType, false)}
                            >
                                <MenuList
                                    autoFocusItem={open}
                                    dense={true}
                                    disablePadding={true}
                                    id="composition-menu"
                                    aria-labelledby="composition-button"
                                    onKeyDown={handleListKeyDown}
                                >
                                    {items?.map((item: TableFilterButtonType, index: number) => (
                                        <MenuItem
                                            sx={{paddingX: "4px"}}
                                            key={index}
                                            onClick={(event) => handleClose(event, {...item})}
                                        >
                                            {mode == "multiple" && (
                                                <Checkbox
                                                    sx={{margin: "0px 4px 0px 0px "}}
                                                    checked={item.tag === 1}
                                                    size={"small"}
                                                />
                                            )}
                                            {item.title}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        );
    };

    return (
        <>
            <Button
                variant="outlined"
                ref={anchorRef}
                id="composition-button"
                aria-controls={open ? "composition-menu" : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                sx={(theme) => ({
                    [`& .${buttonClasses.startIcon} > *:nth-of-type(1)`]: {
                        fontSize: "1.4rem",
                        width: theme.typography.pxToRem(24),
                        height: theme.typography.pxToRem(24),
                    },
                })}
                startIcon={startIcon}
            >
                {title}
            </Button>
            <MenuPopper/>
        </>
    );
};

export default FilterButton;
